import { createForm, type RenderParams } from '@seek/forms-ui';
import type { Response } from '@seek/forms-ui/lib/FormProvider/FormProvider';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import { useState, type FC } from 'react';

import { BUCKETS, type Bucket } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import { TagsList } from '../TagsList';

import translations from './.vocab';

interface BulkMoveDialogProps {
  getSelectedJobApplicationNames: () => string[];
  updateStatusForSelectedApplications: (bucket: Bucket) => Promise<boolean>;
  isOpen: boolean;
  onClose: () => void;
  currentStatus: BUCKETS;
  onClickToastView: (status: Bucket) => void;
}

export const BulkMoveDialog: FC<BulkMoveDialogProps> = ({
  getSelectedJobApplicationNames,
  updateStatusForSelectedApplications,
  currentStatus,
  isOpen,
  onClose,
  onClickToastView,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  const showToast = useToast();

  const [loading, setLoading] = useState(false);

  const selectedApplicationNames = getSelectedJobApplicationNames();

  const submitForm = async (
    formValues: FormValues,
  ): Promise<Response<FormValues>> => {
    setLoading(true);
    const success = await updateStatusForSelectedApplications(
      formValues.status,
    );
    setLoading(false);

    if (success) {
      onClose();
      showToast({
        message: t(
          'selectedJobApplicationsCount application/s moved to newBucket',
          {
            selectedJobApplicationsCount: selectedApplicationNames.length,
            newBucket: getLabelFromBucket(formValues.status),
          },
        ),
        tone: 'positive',
        action: {
          label: t('View'),
          onClick: () => onClickToastView(formValues.status),
        },
        closeLabel: t('Close'),
      });
    }

    return {
      serverErrors: success
        ? undefined
        : {
            status: t(
              'Sorry, we couldn’t save your changes. Can we try again?',
            ),
          },
    };
  };

  return (
    <Dialog
      id="bulk-move-modal"
      data={{ testid: 'bulk-move-modal' }}
      title={t('Move applications')}
      onClose={onClose}
      open={isOpen}
      width="medium"
    >
      <Columns space="small">
        <Column width="2/3">
          <FormProvider initialValues={{ status: currentStatus }}>
            {(formProps) => (
              <FormContent
                loading={loading}
                currentStatus={currentStatus}
                onSubmit={submitForm}
                onClose={onClose}
                {...formProps}
              />
            )}
          </FormProvider>
        </Column>
        <Column width="1/3">
          <Stack space="small">
            <Text weight="strong">
              {t('Move application: labelCount', {
                labelCount: selectedApplicationNames.length,
              })}
            </Text>
            <Box overflow="auto" style={{ maxHeight: '60vh' }}>
              <TagsList names={selectedApplicationNames} />
            </Box>
          </Stack>
        </Column>
      </Columns>
    </Dialog>
  );
};

interface FormValues {
  status: BUCKETS;
}

type FormProps = {
  currentStatus: BUCKETS;
  loading: boolean;
  onClose: () => void;
  onSubmit: (formValues: FormValues) => Promise<Response<FormValues>>;
} & RenderParams<FormValues>;

const { FormProvider, useField } = createForm<FormValues>();

const FormContent: FC<FormProps> = ({
  currentStatus,
  loading,
  onClose,
  onSubmit,
  handleSubmit,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();
  const validBuckets = Object.values(BUCKETS).filter(
    (b) => b !== BUCKETS.UNKNOWN,
  );

  const selectedStatus = useField({
    id: 'status',
    validators: [
      {
        getErrorMessage: ({ formValues: { status } }) =>
          Promise.resolve(
            t(
              'Cannot move selected applications from originalBucket to newBucket.',
              {
                newBucket: getLabelFromBucket(status),
                originalBucket: getLabelFromBucket(currentStatus),
              },
            ),
          ),
        test: ({ formValues: { status } }) => currentStatus !== status,
      },
    ],
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack space="medium">
        <RadioGroup
          id="bulk-update-status-radio-group"
          onChange={selectedStatus.onChange}
          value={selectedStatus.value}
          label={t('Select status')}
        >
          {validBuckets.map((option) => (
            <RadioItem
              key={option}
              label={t('radio group bucket', {
                labelFromBucket: getLabelFromBucket(option),
              })}
              value={option}
            />
          ))}
        </RadioGroup>
        {selectedStatus.errorMessage ? (
          <Text tone="critical">{selectedStatus.errorMessage}</Text>
        ) : null}
        <Inline space="small">
          <Button
            type="submit"
            loading={loading}
            variant="solid"
            tone="formAccent"
          >
            {loading ? t('Moving') : t('Move')}
          </Button>
          <Button
            variant="transparent"
            tone="neutral"
            onClick={() => {
              onClose();
            }}
          >
            {t('Cancel')}
          </Button>
        </Inline>
      </Stack>
    </Box>
  );
};
